/**
 * Helper functions for building grid rows from axis values.
 *
 * Handles the nested loop logic of creating cells for each x/y coordinate.
 */

import {getStepFromSliderStep} from '../../utils/stepUtils';
import {GridCell, GridRow, TransformInput} from '../types';
import {createCellsForMediaKeys, createEmptyCell} from './cells';

// Debug logging flag
const DEBUG = false;
const log = DEBUG ? console.log : () => {};
const warn = DEBUG ? console.warn : () => {};

/**
 * Determines which dimension (step or index) is controlled by a slider.
 *
 * @param xAxis - X-axis type
 * @param yAxis - Y-axis type
 * @returns The controlled dimension
 *
 * @example
 * ```typescript
 * const controlled = getControlledDimension('step', 'run');
 * // Returns 'index' (since step and run are in the grid, index must be controlled)
 * ```
 */
export function getControlledDimension(
  xAxis: string,
  yAxis: string
): 'step' | 'index' {
  // If neither axis is step, then step is controlled
  if (xAxis !== 'step' && yAxis !== 'step') {
    return 'step';
  }
  // If neither axis is index, then index is controlled
  if (xAxis !== 'index' && yAxis !== 'index') {
    return 'index';
  }
  // Default to index
  return 'index';
}

/**
 * Resolves cell coordinates (run, step, index) from x/y axis values.
 *
 * @param xAxis - X-axis type
 * @param xValue - X-axis value
 * @param yAxis - Y-axis type
 * @param yValue - Y-axis value
 * @param controlledDimension - Which dimension is controlled by slider
 * @param currentStep - Current step from slider
 * @param currentIndex - Current index from slider
 * @param runs - Array of runs
 * @returns Cell coordinates or null if unable to resolve
 *
 * @example
 * ```typescript
 * const coords = resolveCellCoordinates(
 *   'step', 100,
 *   'run', runs[2],
 *   'index', 50, 0, runs
 * );
 * // Returns {run: runs[2], step: 100, mediaIndex: 50}
 * ```
 */
export function resolveCellCoordinates(
  xAxis: string,
  xValue: any,
  yAxis: string,
  yValue: any,
  controlledDimension: 'step' | 'index',
  currentStep: number,
  currentIndex: number,
  runs: TransformInput['runs']
): {run: any; step: number; mediaIndex: number} | null {
  let run: any = null;
  let step: number = currentStep;
  let mediaIndex: number = currentIndex;

  // Resolve run
  if (xAxis === 'run') {
    run = xValue;
  } else if (yAxis === 'run') {
    run = yValue;
  } else {
    run = runs[0]; // First run by default
  }

  if (!run) {
    return null;
  }

  // Resolve step
  if (xAxis === 'step') {
    step = xValue;
  } else if (yAxis === 'step') {
    step = yValue;
  } else if (controlledDimension === 'step') {
    step = currentStep;
  }

  // Resolve media index
  if (xAxis === 'index') {
    mediaIndex = xValue;
  } else if (yAxis === 'index') {
    mediaIndex = yValue;
  } else if (controlledDimension === 'index') {
    mediaIndex = currentIndex;
  }

  return {run, step, mediaIndex};
}

/**
 * Gets the snapped step value for a given requested step.
 *
 * @param runStepMeta - Run's step metadata
 * @param requestedStep - Requested step value
 * @returns Snapped step value (nearest available step)
 *
 * @example
 * ```typescript
 * const snapped = getSnappedStep(runMeta, 105);
 * // Returns 100 (if 100 is the nearest step in the run)
 * ```
 */
export function getSnappedStep(
  runStepMeta: any,
  requestedStep: number
): number {
  const {step} = getStepFromSliderStep(runStepMeta, requestedStep, true);
  return step;
}

/**
 * Gets both the snapped step and slider step value.
 * Used when we need to preserve the original slider value (e.g., timestamp).
 *
 * @param runStepMeta - Run's step metadata
 * @param requestedStep - Requested step value
 * @returns Object with both step and sliderStep
 */
export function getSnappedStepWithSliderValue(
  runStepMeta: any,
  requestedStep: number
): {step: number; sliderStep?: number} {
  return getStepFromSliderStep(runStepMeta, requestedStep, true);
}

/**
 * Formats axis value as a human-readable label.
 *
 * @param axis - Axis type
 * @param value - Axis value
 * @returns Formatted label
 *
 * @example
 * ```typescript
 * formatAxisLabel('step', 100);  // Returns "Step 100"
 * formatAxisLabel('run', run);   // Returns run.displayName or run.name
 * ```
 */
export function formatAxisLabel(axis: string, value: any): string {
  if (axis === 'run') {
    return value.displayName || value.name;
  }
  if (axis === 'step') {
    return `Step ${value}`;
  }
  if (axis === 'index') {
    return `Image ${value}`;
  }
  return String(value);
}

/**
 * Formats axis value as a key for row identification.
 *
 * @param axis - Axis type
 * @param value - Axis value
 * @returns Formatted key
 *
 * @example
 * ```typescript
 * formatAxisKey('run', run);  // Returns run.name
 * formatAxisKey('step', 100); // Returns "100"
 * ```
 */
export function formatAxisKey(axis: string, value: any): string {
  if (axis === 'run') {
    return value.name;
  }
  return String(value);
}

/**
 * Builds grid rows from axis values using nested loops.
 *
 * @param xAxis - X-axis type
 * @param yAxis - Y-axis type
 * @param xAxisValues - X-axis values (columns)
 * @param yAxisValues - Y-axis values (rows)
 * @param controlledDimension - Which dimension is controlled by slider
 * @param input - Transform input data
 * @param snapToExistingStep - Whether to snap steps to existing values
 * @param effectiveColumnCount - Optional column count limit (for empty cell padding)
 * @returns Array of grid rows
 */
export function buildGridRows(
  xAxis: string,
  yAxis: string,
  xAxisValues: any[],
  yAxisValues: any[],
  controlledDimension: 'step' | 'index',
  input: TransformInput,
  snapToExistingStep: boolean,
  effectiveColumnCount?: number
): GridRow[] {
  const {runs, stepMeta, config, allMasks, currentStep, currentIndex} = input;
  const {mediaKeys} = config;

  if (!mediaKeys || mediaKeys.length === 0) {
    return [];
  }

  const rows: GridRow[] = [];

  // Nested loop: y-axis (rows) x x-axis (columns)
  for (let y = 0; y < yAxisValues.length; y++) {
    const yValue = yAxisValues[y];
    const cells: GridCell[] = [];

    // Loop up to effectiveColumnCount (or all xAxisValues if no limit)
    // Note: effectiveColumnCount is already divided by mediaKeys.length to account
    // for multiple cells per x-axis value
    const colCount = effectiveColumnCount ?? xAxisValues.length;

    for (let x = 0; x < colCount; x++) {
      // If we've run out of axis data, add empty cells for remaining columns
      // We need to add empty cells for each media key to maintain structure
      if (x >= xAxisValues.length) {
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
        continue;
      }

      const xValue = xAxisValues[x];

      // Resolve run/step/index coordinates for this cell
      const cellCoords = resolveCellCoordinates(
        xAxis,
        xValue,
        yAxis,
        yValue,
        controlledDimension,
        currentStep,
        currentIndex,
        runs
      );

      if (!cellCoords) {
        // Add empty cells for each media key when coordinates can't be resolved
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
        continue;
      }

      // Apply step snapping if needed
      // When run axis is used, we need to find the run index differently:
      // - If run is on x-axis, xValue is the run object at position x in xAxisValues
      // - If run is on y-axis, yValue is the run object at position y in yAxisValues
      // - Otherwise, use the first run (index 0)
      let runIdx: number;
      if (xAxis === 'run') {
        // xValue is the run object from xAxisValues[x], find its index in the original runs array
        runIdx = runs.findIndex(r => r.name === (xValue as any).name);
      } else if (yAxis === 'run') {
        // yValue is the run object from yAxisValues[y], find its index in the original runs array
        runIdx = runs.findIndex(r => r.name === (yValue as any).name);
      } else {
        // No run axis, use first run
        runIdx = 0;
      }

      if (runIdx === -1) {
        // Run not found in runs array - add empty cells and continue
        warn(
          `[gridRowHelpers] Run not found in runs array: ${(cellCoords.run as any)?.name ?? 'unknown'}`
        );
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
        continue;
      }
      const runStepMeta = stepMeta.runs[runIdx];
      let snappedStep: number;
      let sliderStepValue: number | undefined;

      // If the step axis is using slider steps (e.g., timestamps), preserve the slider value
      // and map it to the corresponding step number for metadata lookup
      const isStepAxis = xAxis === 'step' || yAxis === 'step';
      const hasSliderSteps =
        runStepMeta?.sliderSteps && runStepMeta.sliderSteps.length > 0;

      if (isStepAxis && hasSliderSteps) {
        // When step axis uses slider steps, cellCoords.step is already a slider value (e.g., timestamp)
        // IMPORTANT: For tile lookup, we ALWAYS snap to find the correct history row.
        // The snapToExistingStep setting only affects slider display, not tile lookup.
        // We need to:
        // 1. Always snap the slider value to the nearest valid slider step (for finding history rows)
        // 2. Use the snapped slider value as sliderStepValue (for finding history rows)
        // 3. Map it to the actual step number (for backwards compatibility)
        const result = getStepFromSliderStep(
          runStepMeta,
          cellCoords.step,
          true // Always snap for tile lookup, regardless of snapToExistingStep
        );
        snappedStep = result.step;
        // Use the snapped slider step value to match history rows
        sliderStepValue = result.sliderStep;
      } else if (snapToExistingStep) {
        // Regular step snapping (when step is controlled dimension, not on axis)
        const result = getStepFromSliderStep(
          runStepMeta,
          cellCoords.step,
          true
        );
        snappedStep = result.step;
        sliderStepValue = result.sliderStep;
      } else {
        snappedStep = cellCoords.step;
        // If not snapping, the step value might be the slider value
        // Check if we have a custom slider key
        if (config.mediaSliderKey && config.mediaSliderKey !== '_step') {
          sliderStepValue = cellCoords.step;
        }
      }

      // Create cells for ALL media keys (not just the first one)
      // This expands each x-axis value into multiple columns, one per media key
      // Use the run from the runs array to ensure we have the correct reference
      const mediaCells = createCellsForMediaKeys(
        runs[runIdx],
        snappedStep,
        cellCoords.mediaIndex,
        mediaKeys,
        config,
        allMasks,
        sliderStepValue
      );

      if (mediaCells.length > 0) {
        // Add all cells for this x-axis value
        cells.push(...mediaCells);
      } else {
        // If no cells created, add empty cells for each media key to maintain structure
        for (const mediaKey of mediaKeys) {
          cells.push(createEmptyCell(mediaKey));
        }
        warn(
          `[gridRowHelpers] No data for any mediaKey at run=${runs[runIdx]?.name}, step=${snappedStep}, index=${cellCoords.mediaIndex}`
        );
      }
    }

    if (cells.length > 0) {
      rows.push({
        cells,
        rowKey: `${yAxis}-${formatAxisKey(yAxis, yValue)}`,
        rowLabel: formatAxisLabel(yAxis, yValue),
      });
    }
  }

  log(
    `[gridRowHelpers] Built ${rows.length} rows with ${rows[0]?.cells.length ?? 0} columns each`
  );

  return rows;
}
